export const ROLES = {
    CLIENT: 'client',
    THERAPIST: 'therapist',
    CORPORATE_HR: 'corporate_hr',
    ADMIN: 'admin',
};
export const THERAPIST_STATUS = {
    PENDING: 'pending',
    VERIFIED: 'verified',
    SUSPENDED: 'suspended',
};
export const SESSION_TYPE = {
    VIDEO: 'video',
    VOICE: 'voice',
    CHAT: 'chat',
};
export const BOOKING_STATUS = {
    PENDING: 'pending',
    CONFIRMED: 'confirmed',
    COMPLETED: 'completed',
    CANCELLED: 'cancelled',
    DISPUTED: 'disputed',
};
/**
 * System-wide payment outcomes:
 * - pending: waiting on Paystack / webhook (short-lived)
 * - success: money confirmed
 * - failed: charge declined / verification failed
 * - canceled: user closed checkout without paying (abandoned)
 * - refunded: funds returned after a successful charge
 */
export const PAYMENT_STATUS = {
    PENDING: 'pending',
    SUCCESS: 'success',
    FAILED: 'failed',
    CANCELED: 'canceled',
    REFUNDED: 'refunded',
};
/** Alias for abandoned checkout — same persisted status as canceled. */
export const PAYMENT_ABANDONED = PAYMENT_STATUS.CANCELED;
export const ESCROW_HOLD_DURATION_MS = 24 * 60 * 60 * 1000;
export const BOOKING_SLOT_LOCK_MS = 10 * 60 * 1000;
/** Hours before session for full refund / free reschedule. */
export const CANCELLATION_FULL_REFUND_HOURS = 24;
export const RESCHEDULE_FREE_HOURS = 24;
/** One courtesy late cancellation allowed within this window (months). */
export const COURTESY_LATE_CANCEL_MONTHS = 12;
/** @deprecated Prefer wallet credit + no late cash refund. Kept for legacy references. */
export const CANCELLATION_PARTIAL_REFUND_HOURS = 0;
/** @deprecated Late cancels no longer use a partial cash refund. */
export const PARTIAL_REFUND_PERCENT = 0;
export const DEFAULT_COMMISSION_PERCENT = 25;
export const SOAP_NOTE_EDIT_WINDOW_MS = 24 * 60 * 60 * 1000;
export const REFERRAL_CREDIT_AMOUNT = 2500;
export const REFERRAL_CREDIT_EXPIRY_DAYS = 90;
export const MAX_GROUP_PARTICIPANTS = 8;
export const GROUP_MIN_PARTICIPANTS = 3;
export const GROUP_CANCELLATION_NOTICE_HOURS = 48;
export const GROUP_CANCELLATION_REFUND_PERCENT = 50;
export const ASSESSMENT_SEVERE_PHQ9_THRESHOLD = 20;
export const ASSESSMENT_SEVERE_GAD7_THRESHOLD = 15;
export const ASSESSMENT_SIGNIFICANT_INCREASE = 5;
export const WEEKLY_CHECKIN_CRON = '0 9 * * 1';
export const REFERRAL_FRAUD_IP_LIMIT = 5;
export const REFERRAL_FRAUD_IP_WINDOW_DAYS = 7;
export const K_ANONYMITY_FLOOR = 5;
export const HMO_RATE_LIMIT = 1000;
export const HMO_RATE_WINDOW_MS = 60 * 60 * 1000;
export const HMO_KEY_ROTATION_DAYS = 90;
export const HMO_API_KEY_BITS = 256;
export const MATCHING_EXPLANATION_COUNT = 3;
export const MATCHING_WEIGHTS = {
    specialization: 0.35,
    language: 0.25,
    rating: 0.2,
    acceptanceRate: 0.1,
    priceFit: 0.1,
};
export const MATCHING_DEFAULT_PRICE_MAX_NGN = 100_000;
export const AI_TIMEOUT_MS = 30000;
export const EMBEDDING_CACHE_TTL_S = 86400;
export const CRISIS_HOTLINE_NUMBERS = {
    NIGERIA: '0800 800 8000',
    LAGOS: '0900 000 0000',
    GENERAL: '0800 111 1111',
};
export const EXPANSION_CURRENCIES = ['NGN', 'GHS', 'XOF'];
export const MARKETPLACE_TAGLINE = "Nigeria's trusted mental health marketplace";
export const BIO_MIN_CHARS = 50;
export const BIO_MAX_CHARS = 4000;
export const BIO_MAX_WORDS = 600;
export function countWords(value) {
    const trimmed = value.trim();
    if (!trimmed)
        return 0;
    return trimmed.split(/\s+/).length;
}
export const LICENSE_BODY_OPTIONS = [
    { id: 'CASSON', label: 'CASSON (Nigeria)' },
    { id: 'NACP', label: 'NACP (Nigeria)' },
    { id: 'GPC', label: 'Ghana Psychology Council' },
    { id: 'other', label: 'Other recognised body' },
];
/** Shown after "Other recognised body" is selected. */
export const OTHER_LICENSE_BODIES = [
    { id: 'MDCN', label: 'Medical and Dental Council of Nigeria' },
    { id: 'NMCN', label: 'Nursing and Midwifery Council of Nigeria' },
    { id: 'APN', label: 'Association of Psychiatrists in Nigeria' },
    { id: 'NPA', label: 'Nigerian Psychological Association' },
];
export const OTHER_LICENSE_SENTINEL = 'other';
export const CUSTOM_LICENSE_BODY = '__custom__';
export const LICENSE_BODY_NAME_MAX = 80;
const PRIMARY_LICENSE_IDS = ['CASSON', 'NACP', 'GPC'];
export function licenseBodyLabel(id) {
    const primary = LICENSE_BODY_OPTIONS.find((body) => body.id === id);
    if (primary && primary.id !== OTHER_LICENSE_SENTINEL)
        return primary.label;
    const known = OTHER_LICENSE_BODIES.find((body) => body.id === id);
    if (known)
        return known.label;
    return id;
}
export function licenseBodiesAreComplete(bodies) {
    return bodies.length > 0 && bodies.every((id) => id !== OTHER_LICENSE_SENTINEL);
}
export function isPrimaryLicenseBody(id) {
    return PRIMARY_LICENSE_IDS.includes(id);
}
export const CLINICIAN_PHOTO_PX = 800;
export const CLINICIAN_PHOTO_BACKGROUND = '#F3F0E8';
export const CLINICIAN_PHOTO_SPEC = 'Square photo, face centered, plain light background. Every photo is cropped to 800×800.';
export const SESSION_FORMAT_CHOICES = [
    { id: 'video', label: 'Video' },
    { id: 'voice', label: 'Audio' },
    { id: 'chat', label: 'Chat' },
];
export const REVIEW_POINTS = 15;
export const JOURNAL_STREAK_DAYS = 30;
export const JOURNAL_STREAK_POINTS = 50;
export const PAYMENT_POLICY_SUMMARY = [
    'You pay before the session is confirmed.',
    'Cancel more than 24 hours before the start time and the full amount returns to your wallet.',
    'Cancel inside 24 hours and there is no cash refund. You can use one courtesy late cancellation every 12 months for wallet credit.',
    'If you miss the session, there is no refund and the clinician is paid.',
    'You can reschedule free of charge up to 24 hours before the appointment.',
];
/** Primary reassurance on therapist onboarding and trust surfaces */
export const LICENSE_VERIFICATION_REASSURANCE = 'Verification through CASSON, NACP, and other recognised license bodies keeps our community trusted.';
/** Short badge for footers, hero chips, and compact UI */
export const LICENSE_VERIFICATION_BADGE = 'CASSON, NACP & licensed bodies';
/** Therapist onboarding subtitle */
export const LICENSE_VERIFICATION_ONBOARDING_SUBTITLE = 'Complete your profile for verification through CASSON, NACP, and other recognised license bodies';
/** Terms eligibility copy for therapist accounts */
export const LICENSE_VERIFICATION_TERMS_ELIGIBILITY = 'Therapists must hold a valid registration with CASSON, NACP, or another recognised license body and complete our verification process.';
/** Browse / discovery hero line */
export const LICENSE_VERIFICATION_HERO_SHORT = 'Therapists verified through CASSON, NACP, and other recognised license bodies';
/** Home page hero paragraph */
export const LICENSE_VERIFICATION_HOME_HERO = 'Connect with therapists verified through CASSON, NACP, and other recognised license bodies, take clinical assessments, and start your journey to better mental health — all from the comfort of your home.';
/** Auth aside pillar (mobile + compact surfaces) */
export const LICENSE_VERIFICATION_PILLAR = 'Verified through CASSON, NACP, and other license bodies.';
/** States a clinician can list as their base. Abuja is the FCT. Ibadan stays for profiles already saved with that city. Sessions are still online. */
export const PRACTICE_CITY_LABELS = [
    'Abia',
    'Abuja',
    'Adamawa',
    'Akwa Ibom',
    'Anambra',
    'Bauchi',
    'Bayelsa',
    'Benue',
    'Borno',
    'Cross River',
    'Delta',
    'Ebonyi',
    'Edo',
    'Ekiti',
    'Enugu',
    'Gombe',
    'Ibadan',
    'Imo',
    'Jigawa',
    'Kaduna',
    'Kano',
    'Katsina',
    'Kebbi',
    'Kogi',
    'Kwara',
    'Lagos',
    'Nasarawa',
    'Niger',
    'Ogun',
    'Ondo',
    'Osun',
    'Oyo',
    'Plateau',
    'Rivers',
    'Sokoto',
    'Taraba',
    'Yobe',
    'Zamfara',
];
export function practiceLocationLabel(value) {
    if (value === 'Abuja')
        return 'Abuja (FCT)';
    return value;
}
export const PRACTICE_CITIES = [
    { slug: 'lagos', label: 'Lagos' },
    { slug: 'abuja', label: 'Abuja' },
    { slug: 'ibadan', label: 'Ibadan' },
];
export const DIRECTORY_LANGUAGES = [
    { slug: 'yoruba', code: 'yo', label: 'Yoruba' },
    { slug: 'igbo', code: 'ig', label: 'Igbo' },
    { slug: 'pidgin', code: 'pcm', label: 'Pidgin' },
];
export const DIRECTORY_NEEDS = [
    { slug: 'anxiety', specialization: 'anxiety', label: 'Anxiety' },
    { slug: 'depression', specialization: 'depression', label: 'Depression' },
    { slug: 'trauma', specialization: 'trauma_ptsd', label: 'Trauma' },
    { slug: 'bipolar', specialization: 'bipolar', label: 'Bipolar' },
    { slug: 'eating', specialization: 'eating_disorders', label: 'Eating' },
    { slug: 'sleep', specialization: 'sleep_disorders', label: 'Sleep' },
];
//# sourceMappingURL=constants.js.map